import { useState, type FormEvent } from "react";

import { api, errorMessage } from "../api/client";
import { AsyncBlock } from "../components/AsyncBlock";
import { Panel } from "../components/Panel";
import { Select } from "../components/Select";
import { formatDateTime } from "../lib/format";
import { ui } from "../lib/ui";
import { useAsync } from "../lib/useAsync";
import type { Release, ReleaseCreate, ReleaseKind } from "../types";

const EMPTY: ReleaseCreate = {
  version: "",
  kind: "update",
  runtime: "",
  object_key: "",
  sha256: "",
  size_bytes: 0,
  notes: "",
  make_current: true,
};

function formatSize(bytes: number): string {
  if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(2)} GB`;
  if (bytes >= 1e6) return `${(bytes / 1e6).toFixed(1)} MB`;
  return `${Math.round(bytes / 1e3)} kB`;
}

/** Which installer branch PCs get. `installer\publish.py` uploads the files
 *  and registers them here; this page is for looking, switching the current
 *  one, and downloading a Setup for a new PC. */
export function Releases() {
  const releases = useAsync(() => api.listReleases(), []);
  const [form, setForm] = useState<ReleaseCreate>(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const rows = releases.data ?? [];
  const current = (kind: ReleaseKind) => rows.find((r) => r.kind === kind && r.is_current);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      releases.reload();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    act(async () => {
      await api.createRelease({ ...form, sha256: form.sha256.trim().toLowerCase() });
      setForm(EMPTY);
      setShowForm(false);
    });
  };

  const remove = (row: Release) => {
    if (!window.confirm(`Forget ${row.kind} ${row.version}? The file stays in the bucket.`)) return;
    act(() => api.deleteRelease(row.id));
  };

  const update = current("update");
  const setup = current("setup");

  return (
    <div className="flex flex-col gap-5">
      <Panel
        title="Current release"
        subtitle="A branch PC whose runtime fingerprint is one the Update fits gets the Update (a few MB); any other gets the full Setup — never an NVIDIA (CUDA) PC, whose Setup is handed out directly. PCs check a few times a day and install at 03:00 local."
      >
        <div className="grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-4">
          {(["update", "setup"] as const).map((kind) => {
            const row = kind === "update" ? update : setup;
            return (
              <div key={kind} className="rounded-lg border border-border bg-bg px-4 py-3">
                <div className={ui.label}>{kind === "update" ? "Update" : "Full Setup"}</div>
                {row ? (
                  <>
                    <div className="mt-1 text-lg font-semibold">{row.version}</div>
                    <div className="text-xs text-muted">
                      {formatSize(row.size_bytes)} · runtime <code>{row.runtime}</code>
                    </div>
                    <a className={`${ui.btnQuiet} mt-3 text-xs`} href={api.releaseDownloadUrl(row.id)}>
                      Download
                    </a>
                  </>
                ) : (
                  <div className="mt-1 text-muted">none yet</div>
                )}
              </div>
            );
          })}
        </div>
        {update && setup && !update.runtime.split(" ").includes(setup.runtime) && (
          <p className={`${ui.notice} ${ui.noticeError} mt-4`}>
            The current Update and Setup have different runtime fingerprints: PCs on the
            Setup's runtime will never take this Update. Publish both from the same build.
          </p>
        )}
      </Panel>

      <Panel
        title="All releases"
        subtitle="Registered installers in the bucket. Normally added by installer\publish.py after a build."
        actions={
          <div className="flex gap-2">
            <button type="button" className={ui.btnQuiet} onClick={releases.reload}>
              Refresh
            </button>
            <button type="button" className={ui.btnQuiet} onClick={() => setShowForm((v) => !v)}>
              {showForm ? "Cancel" : "Register by hand"}
            </button>
          </div>
        }
      >
        {error && <p className={`${ui.notice} ${ui.noticeError}`}>{error}</p>}

        {showForm && (
          <form onSubmit={submit} className={`${ui.toolbar} mb-4 items-end`}>
            <label className={ui.field}>
              Version
              <input className={ui.input} required value={form.version}
                onChange={(e) => setForm({ ...form, version: e.target.value })} placeholder="1.2.0" />
            </label>
            <label className={ui.field}>
              Kind
              <Select value={form.kind}
                onChange={(e) => setForm({ ...form, kind: e.target.value as ReleaseKind })}>
                <option value="update">update</option>
                <option value="setup">setup</option>
              </Select>
            </label>
            <label className={ui.field}>
              Runtime fingerprint
              <input className={ui.input} required value={form.runtime}
                onChange={(e) => setForm({ ...form, runtime: e.target.value })}
                placeholder="1-3.12.10-…" />
            </label>
            <label className={ui.field}>
              Object key
              <input className={ui.input} required value={form.object_key}
                onChange={(e) => setForm({ ...form, object_key: e.target.value })}
                placeholder="releases/1.2.0/ElEzabyRetailAnalytics-Update-1.2.0.exe" />
            </label>
            <label className={ui.field}>
              SHA-256
              <input className={`${ui.input} min-w-[320px] font-mono text-xs`} required
                value={form.sha256} onChange={(e) => setForm({ ...form, sha256: e.target.value })} />
            </label>
            <label className={ui.field}>
              Size (bytes)
              <input className={ui.input} type="number" min={0} required value={form.size_bytes}
                onChange={(e) => setForm({ ...form, size_bytes: Number(e.target.value) })} />
            </label>
            <label className={ui.field}>
              Notes
              <input className={ui.input} value={form.notes ?? ""}
                onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.make_current ?? false}
                onChange={(e) => setForm({ ...form, make_current: e.target.checked })} />
              Make current
            </label>
            <button type="submit" className={ui.btn} disabled={busy}>
              Register
            </button>
          </form>
        )}

        <AsyncBlock loading={releases.loading} error={releases.error} empty={rows.length === 0}
          emptyMessage="No releases registered yet.">
          <div className="overflow-x-auto">
            <table className={ui.table}>
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Kind</th>
                  <th>Runtime</th>
                  <th>Size</th>
                  <th>Published</th>
                  <th>Notes</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="font-medium">
                      {row.version}
                      {row.is_current && (
                        <span className={`${ui.pill} ${ui.pillSucceeded} ml-2`}>current</span>
                      )}
                    </td>
                    <td>{row.kind}</td>
                    <td><code className="text-xs">{row.runtime}</code></td>
                    <td title={`${row.size_bytes} bytes · sha256 ${row.sha256}`}>
                      {formatSize(row.size_bytes)}
                    </td>
                    <td>{formatDateTime(row.published_at)}</td>
                    <td className="max-w-[260px] text-xs text-muted">{row.notes}</td>
                    <td className="whitespace-nowrap text-right">
                      <a className={`${ui.btnQuiet} px-2.5 py-1 text-xs`}
                        href={api.releaseDownloadUrl(row.id)}>
                        Download
                      </a>
                      {!row.is_current && (
                        <button type="button" className={`${ui.btnQuiet} ml-1.5 px-2.5 py-1 text-xs`}
                          disabled={busy} onClick={() => act(() => api.makeCurrentRelease(row.id))}>
                          Make current
                        </button>
                      )}
                      <button type="button" className={`${ui.btnDanger} ml-1.5 px-2.5 py-1 text-xs`}
                        disabled={busy} onClick={() => remove(row)}>
                        Forget
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AsyncBlock>
      </Panel>
    </div>
  );
}
